import { useEffect, useState } from "react";
import styles from "./create-insight.module.css";
import GridList from "../../../../components/AdminPanel/CreateInsights/grid-list";
import NewInsight from "../../../../components/AdminPanel/CreateInsights/new-insight";
import { IPostInsight } from "../../../../interfaces/insightPost";
import { IUser } from "../../../../interfaces/user";

type Props = {
  user: IUser;
  selected: string;
};

function formatDateType(date: string) {
  const dateObj = new Date(date);
  dateObj.setMinutes(dateObj.getMinutes() + dateObj.getTimezoneOffset()); // Adjust for timezone offset
  const day = String(dateObj.getDate()).padStart(2, "0");
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const year = dateObj.getFullYear();
  return `${day}/${month}/${year}`;
}

function getCookie(name: string) {
  const match = document.cookie.match(`(^|;)\\s*${name}=([^;]+)`);
  return match ? match[2] : null;
}

const CreateInsight = ({ user, selected }: Props) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [insightList, setInsightList] = useState<IPostInsight[]>([]);
  const [insight, setInsight] = useState<IPostInsight>({});
  const [isEdit, setIsEdit] = useState<boolean>(false);

  const onLoadInsights = async (token: string) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) {
        console.error("Erro: API URL não definida.");
        return;
      }

      const response = await fetch(`${apiUrl}/posts`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const resData = await response.json();

      if (!response.ok) {
        alert(
          `${resData.message} ${resData.details}` || "Erro ao buscar insights."
        );
        return;
      }

      resData.data.forEach((el: IPostInsight) => {
        if (el.date) {
          el.date = formatDateType(el.date);
          el.username = el.user
            ? `${el.user.firstname} ${el.user.lastname}`
            : "Unknown User";
        }
      });

      return resData.data as IPostInsight[];
    } catch (error) {
      alert("Erro ao buscar insights: " + error);
      return;
    }
  };

  const onEditInsight = (id: string) => {
    const selectedInsight = insightList.find((el) => el._id === id);
    if (selectedInsight) {
      setInsight(selectedInsight);
      setIsEdit(true);
    }
  };

  const onRemoveInsight = async (id: string) => {
    try {
      const token = getCookie("token");
      if (!token) {
        alert("Erro: Token não encontrado.");
        return;
      }

      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) {
        console.error("Erro: API URL não definida.");
        return;
      }

      const response = await fetch(`${apiUrl}/posts?id=${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const resData = await response.json();

      if (!response.ok) {
        alert(
          `${resData.message} ${resData.details}` || "Erro ao deletar insight."
        );
        return;
      }

      onLoadInsights(token)
        .then((postsList) => {
          if (postsList) {
            setInsightList(postsList);
          }
        })
        .catch((error) => {
          console.error("Erro ao buscar insights: ", error);
          setInsightList([]);
        });

      return;
    } catch (error) {
      alert("Erro ao salvar insight: " + error);
      return;
    }
  };

  const onHandleAuthentication = async (token: string) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) {
        console.error("Erro: API URL não definida.");
        return;
      }

      const response = await fetch(`${apiUrl}/auth-check`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        alert("Erro ao validar autenticação: " + response.statusText);
        return;
      }

      const resData = await response.json();

      return resData;
    } catch (error) {
      alert("Erro ao validar autenticação: " + error);
      return;
    }
  };

  const onUploadImage = async (file: File, token: string) => {
    const auth = await onHandleAuthentication(token);
    if (auth && auth.details === "Token válido.") {
      try {
        const cloudname = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || "";
        const uploadPreset = process.env.REACT_APP_CLOUDINARY_PRESET || "";

        const formData = new FormData();
        formData.append("file", file);
        formData.append("upload_preset", uploadPreset);

        const url = `https://api.cloudinary.com/v1_1/${cloudname}/image/upload`;

        const response = await fetch(url, {
          method: "POST",
          body: formData,
        });

        const data = await response.json();

        if (response.ok) {
          return data.secure_url;
        } else {
          alert("Erro ao fazer upload da imagem: " + data.error.message);
          return null;
        }
      } catch (error) {
        alert("Erro ao fazer upload da imagem: " + error);
        return null;
      }
    }
  };

  const onUploadInsight = async (oldInsight: IPostInsight) => {
    try {
      const token = getCookie("token");
      if (!token) {
        alert("Erro: Token não encontrado.");
        return;
      }

      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) {
        console.error("Erro: API URL não definida.");
        return;
      }

      if (oldInsight.coverFile && oldInsight.coverFile instanceof File) {
        const coverUrl = await onUploadImage(oldInsight.coverFile, token);
        if (coverUrl) {
          oldInsight.cover = coverUrl;
          oldInsight.coverFile = null;
        } else {
          return;
        }
      }

      if (oldInsight.paragraphs && oldInsight.paragraphs.length > 0) {
        const imageUploadPromises = oldInsight.paragraphs
          .filter(
            (paragraph) =>
              paragraph.type === "image" && paragraph.file instanceof File
          )
          .map(async (paragraph) => {
            if (!paragraph.file) return;
            const imageUrl = await onUploadImage(paragraph.file, token);
            if (imageUrl) {
              paragraph.text = imageUrl;
              paragraph.file = null;
            }
          });

        await Promise.all(imageUploadPromises);
      }

      const response = await fetch(`${apiUrl}/posts?id=${oldInsight._id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(oldInsight),
      });

      const resData = await response.json();

      if (!response.ok) {
        setInsight(oldInsight);
        alert(
          `${resData.message} ${resData.details}` ||
            "Erro ao atualizar insight."
        );
        return;
      }

      setInsight({} as IPostInsight);

      onLoadInsights(token)
        .then((postsList) => {
          if (postsList) {
            setInsightList(postsList);
          }
        })
        .catch((error) => {
          console.error("Erro ao buscar insights: ", error);
          setInsightList([]);
        });

      return;
    } catch (error) {
      alert("Erro ao atualizar insight: " + error);
      return;
    }
  };

  const onSaveInsight = async (newInsight: IPostInsight) => {
    try {
      const token = getCookie("token");
      if (!token) {
        alert("Erro: Token não encontrado.");
        return;
      }

      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) {
        console.error("Erro: API URL não definida.");
        return;
      }

      if (newInsight.coverFile && newInsight.coverFile instanceof File) {
        const coverUrl = await onUploadImage(newInsight.coverFile, token);
        if (coverUrl) {
          newInsight.cover = coverUrl;
        } else {
          return;
        }
      }

      if (newInsight.paragraphs && newInsight.paragraphs.length > 0) {
        const imageUploadPromises = newInsight.paragraphs
          .filter(
            (paragraph) =>
              paragraph.type === "image" && paragraph.file instanceof File
          )
          .map(async (paragraph) => {
            if (!paragraph.file) return;
            const imageUrl = await onUploadImage(paragraph.file, token);
            if (imageUrl) {
              paragraph.text = imageUrl;
            }
          });

        await Promise.all(imageUploadPromises);
      }

      const response = await fetch(`${apiUrl}/posts`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(newInsight),
      });

      const resData = await response.json();

      if (!response.ok) {
        alert(
          `${resData.message} ${resData.details}` || "Erro ao salvar insight."
        );
        return;
      }

      setInsight({} as IPostInsight);

      onLoadInsights(token)
        .then((postsList) => {
          if (postsList) {
            setInsightList(postsList);
          }
        })
        .catch((error) => {
          console.error("Erro ao buscar insights: ", error);
          setInsightList([]);
        });

      return;
    } catch (error) {
      alert("Erro ao salvar insight: " + error);
      return;
    }
  };

  const onClickNewInsight = () => {
    setInsight({} as IPostInsight);
    setIsEdit(true);
  };

  const onClickSairInsight = () => {
    setInsight({} as IPostInsight);
    setIsEdit(false);
  };

  const onRenderInsights = () => {
    return (
      <section className={styles.createInsight}>
        <div className={styles.header}>
          <h4 className={styles.title}>Meus Insights</h4>
          <div className={styles.action_btn_div}>
            <button className={styles.action_btn} onClick={onClickNewInsight}>
              Novo Insight
            </button>
          </div>
        </div>
        <div className={styles.list_container}>
          <GridList
            insightList={insightList}
            onEditInsight={onEditInsight}
            onRemoveInsight={onRemoveInsight}
          />
        </div>
      </section>
    );
  };

  const onRenderInsightForm = () => {
    return (
      <section className={styles.createInsight}>
        <div className={styles.header}>
          <h4 className={styles.title}>Meus Insights</h4>
        </div>
        <div className={`${styles.list_container}`}>
          <NewInsight
            user={user}
            insight={insight}
            onClickSairInsight={onClickSairInsight}
            onSaveInsight={onSaveInsight}
            onUploadInsight={onUploadInsight}
            setIsEdit={setIsEdit}
          />
        </div>
      </section>
    );
  };

  const LoadingOverlay = () => (
    <div className={styles.loadingOverlay}>
      <div className={styles.spinner}></div>
    </div>
  );

  useEffect(() => {
    if (selected === "Insights") {
      const token = getCookie("token");
      if (token) {
        setLoading(true);
        onLoadInsights(token)
          .then((postsList) => {
            if (postsList) setInsightList(postsList);
          })
          .catch((error) => {
            console.error("Erro ao buscar insights: ", error);
            setInsightList([]);
          })
          .finally(() => {
            setLoading(false);
          });
      } else {
        setInsightList([]);
      }
    }
  }, [selected]);

  return (
    <>
      {loading && <LoadingOverlay />}
      {isEdit ? onRenderInsightForm() : onRenderInsights()}
    </>
  );
};

export default CreateInsight;
